import { SlotImage, usePageAsset } from "~/components/ui";

type FounderSectionProps = {
  eyebrow: string;
  titleLead: string;
  titleAccent: string;
  /** Omitted in the `compact` variant (Método, S4): no pull quote. */
  quote?: string;
  body: string;
  signature: string;
  /** `editorial` (Home): portrait beside heading, quote and body. `compact` (Método): a
   *  smaller portrait first, then the copy — the delivery's `.gala-inline`. */
  variant?: "editorial" | "compact";
};

/**
 * T872 S3 — «Quién está detrás», the editorial variant of the October delivery
 * (`components/FounderSection.html`): heading, pull quote, one paragraph and the
 * signature beside the portrait from the `founder` slot. Copy arrives as props because
 * Home and Método say different things with the same shape (S4 adds the compact variant).
 * An empty slot — or one whose variant has no URL — renders no figure. On a phone the portrait sits between the heading and
 * the quote (the delivery's grid rows), which is why the copy is `display: contents` there.
 */
export function FounderSection({ eyebrow, titleLead, titleAccent, quote, body, signature, variant = "editorial" }: FounderSectionProps) {
  // Gate on the variant's URL, not on the record: a published asset whose variant never
  // resolved (no Images account hash locally, a failed derivative) must leave no grey box.
  const portrait = usePageAsset("founder")?.imageUrls?.heroMobile;
  return (
    // `/conoceme` lands on this `id` (T762); `.mt-page` / `.ho-page section[id]` clear the header.
    <section id="pub-founder" className={variant === "compact" ? "pub-founder is-compact" : "pub-founder"} aria-labelledby="pub-founder-title">
      <div className="pub-wrap pub-founder-layout">
        <div className="pub-founder-copy">
          <div className="pub-founder-heading">
            <p className="pub-eyebrow">{eyebrow}</p>
            <h2 id="pub-founder-title">
              {titleLead} <em>{titleAccent}</em>
            </h2>
          </div>
          {quote ? <blockquote className="pub-founder-quote">{quote}</blockquote> : null}
          <p className="pub-founder-body">{body}</p>
          <p className="pub-founder-signature">{signature}</p>
        </div>
        {portrait ? (
          <figure className="pub-founder-photo">
            <SlotImage slot="founder" variant="heroMobile" loading="lazy" />
          </figure>
        ) : null}
      </div>
    </section>
  );
}
